import { useCallback, useEffect, useState } from 'react'
import { initTelegram } from '../platform/telegram'
import { NavBar } from '../shared/NavBar'
import type { Tab } from '../shared/NavBar'
import { TopBar } from '../shared/TopBar'
import type { AnyGameModule, SessionStats } from '../core/contracts'
import type { SessionSummary } from '../core/insights'
import { gameById } from '../registry'
import { useProgress } from './useProgress'
import { useWorkout } from './useWorkout'
import { TodayScreen } from './screens/TodayScreen'
import { GamesScreen } from './screens/GamesScreen'
import { ProgressScreen } from './screens/ProgressScreen'
import { PlayScreen } from './screens/PlayScreen'
import { DaySummary } from './screens/DaySummary'
import { Onboarding } from './screens/Onboarding'
import { KEYS, store } from '../platform/storage'
import { reportWorkoutDone } from '../platform/api'
import './App.css'

/**
 * Сессия: свободная игра или прохождение программы дня.
 * Разница в том, что предлагает экран результата - переиграть
 * или перейти к следующему упражнению.
 */
type Session =
  | { kind: 'free'; game: AnyGameModule }
  | { kind: 'workout'; game: AnyGameModule }

export function App() {
  const [tab, setTab] = useState<Tab>('today')
  const [session, setSession] = useState<Session | null>(null)
  const [showSummary, setShowSummary] = useState(false)
  // null - ещё не знаем, показывать ли вводный экран
  const [onboarded, setOnboarded] = useState<boolean | null>(null)
  // заходы текущей тренировки - основа для разбора на итоговом экране
  const [sessions, setSessions] = useState<readonly SessionSummary[]>([])

  const progress = useProgress()
  const workout = useWorkout(progress.weakest)

  useEffect(() => {
    initTelegram()
  }, [])

  useEffect(() => {
    let alive = true
    void (async () => {
      let seen = false
      try {
        const profile = await store.get<{ onboarded?: boolean }>(KEYS.profile, {})
        seen = profile.onboarded === true
      } catch {
        // хранилище недоступно - лучше показать вводный экран, чем сломаться
      }
      if (alive) setOnboarded(seen)
    })()
    return () => {
      alive = false
    }
  }, [])

  /**
   * Вводный экран пройден: запоминаем и сразу открываем первое упражнение.
   * Кнопка обещает начать тренировку - значит она должна начаться,
   * а не высадить человека на главный экран.
   */
  const finishOnboarding = useCallback(() => {
    setOnboarded(true)
    void store.set(KEYS.profile, { onboarded: true }).catch(() => {
      // не сохранилось - вводный экран покажется ещё раз, не критично
    })
    const id = workout.nextGameId
    const game = id ? gameById(id) : null
    if (game) setSession({ kind: 'workout', game })
  }, [workout.nextGameId])

  /**
   * Заход закончен.
   *
   * Отмечаем упражнение пройденным прямо здесь, а не по нажатию
   * «Следующее»: упражнение завершено в момент последнего раунда,
   * кнопка - это только навигация. Раньше выход сразу после игры
   * терял прогресс по программе дня.
   */
  const recordSession = useCallback(
    (game: AnyGameModule, inWorkout: boolean) =>
      (stats: SessionStats, startLevel: number) => {
        setSessions((prev) => [
          ...prev,
          { gameId: game.id, discipline: game.discipline, stats, startLevel },
        ])
        if (inWorkout) void workout.complete(game.id)
      },
    [workout],
  )

  const startWorkout = useCallback(() => {
    setSessions([])
    const id = workout.nextGameId
    const game = id ? gameById(id) : null
    if (game) setSession({ kind: 'workout', game })
  }, [workout.nextGameId])


  const exitSession = useCallback(() => {
    setSession(null)
    progress.reload()
    // программа могла завершиться последним упражнением - показываем итог,
    // даже если человек вышел кнопкой, а не «Следующее»
    if (workout.done) setShowSummary(true)
  }, [progress, workout.done])

  /** Упражнение пройдено: отмечаем в программе и ведём к следующему. */
  /** Переход к следующему упражнению. Отметка уже поставлена в recordSession. */
  const advanceWorkout = useCallback(() => {
    if (session?.kind !== 'workout') return
    progress.reload()

    const rest = workout.state?.plan.gameIds.filter(
      (id) => id !== session.game.id && !(workout.state?.completed ?? []).includes(id),
    )
    const nextGame = rest?.[0] ? gameById(rest[0]) : null

    if (nextGame) {
      setSession({ kind: 'workout', game: nextGame })
    } else {
      // программа пройдена - закрываем цикл итогом, а не молчаливым выходом
      setSession(null)
      setShowSummary(true)
      // сообщаем серверу, что сегодня тренировка пройдена -
      // иначе вечером придёт напоминание о сделанном
      void reportWorkoutDone()
    }
  }, [session, workout, progress])

  // ждём ответа хранилища, чтобы не мигнуть главным экраном перед вводным
  if (onboarded === null) return null

  if (!onboarded) {
    return <Onboarding onStart={finishOnboarding} />
  }

  if (showSummary) {
    return (
      <DaySummary
        progress={progress}
        streak={workout.streak}
        sessions={sessions}
        onDone={() => {
          setShowSummary(false)
          setSessions([])
        }}
      />
    )
  }

  if (session) {
    const remaining =
      session.kind === 'workout'
        ? (workout.state?.plan.gameIds.length ?? 0) -
          (workout.state?.completed.length ?? 0) -
          1
        : 0

    return (
      <PlayScreen
        game={session.game}
        onExit={exitSession}
        onSession={recordSession(session.game, session.kind === 'workout')}
        {...(session.kind === 'workout'
          ? { onNext: advanceWorkout, remaining }
          : {})}
      />
    )
  }

  return (
    <div className="app">
      <TopBar />
      <main className="app__body">
        {tab === 'today' && (
          <TodayScreen
            progress={progress}
            workout={workout}
            onPlay={(game) => setSession({ kind: 'workout', game })}
            onStartWorkout={startWorkout}
          />
        )}
        {tab === 'games' && (
          <GamesScreen onPlay={(game) => setSession({ kind: 'free', game })} />
        )}
        {tab === 'progress' && <ProgressScreen progress={progress} />}
      </main>
      <NavBar active={tab} onChange={setTab} />
    </div>
  )
}
